import { memo, useMemo } from "react";
import styled from "styled-components";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { External, Spinner } from "@sablier/v2-components/atoms";
import { Button, Label } from "@sablier/v2-components/molecules";
import useChainExplorer from "@sablier/v2-hooks/useChainExplorer";
import { Trans, useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { IAddress } from "@sablier/v2-types";
import { FEATURE_EMPTY_PROXY_ENABLED } from "~/client/constants";
import { ProxyBankFormProvider as Provider } from "~/client/contexts";
import { useAccount } from "~/client/hooks";
import Confirmation from "./Confirmation";
import Form from "./Form";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
  }
`;

const Section = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
    & > *[data-component="button"] {
      justify-content: flex-start;
      &:not([data-mini="true"]) {
        width: 100%;
      }
    }
  }
`;

const Description = styled.div`
  & > p {
    ${(props) => props.theme.styles.textParagraph}
    & {
      color: ${(props) => props.theme.colors.gray200};
      line-height: 20pt;
    }
  }
`;

const Loader = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    justify-content: center;
    width: 100%;
    height: 300px;
  }
`;

const Checkout = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge});
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    border: 2px solid ${(props) => props.theme.colors.border};
    border-radius: 8px;
  }
`;

const Wrapper = styled(WrapperPartial)``;

const Info = memo(function Info({
  chainId,
  proxy,
}: {
  chainId: number | undefined;
  proxy: IAddress | undefined;
}) {
  const explorer = useChainExplorer({ chainId, type: "address", hash: proxy });

  return (
    <div>
      <Trans
        i18nKey={"structs.viewOnExplorer"}
        components={[<External key={"0"} to={explorer}></External>]}
      />
    </div>
  );
});

function ButtonMissing() {
  const { t } = useT();

  return (
    <Button
      accent={"dark200"}
      appearance={"solid"}
      isDisabled
      isPreview
      isMini
      purpose={"button"}
      title={t("modalCards.proxy.bank.missing")}
    />
  );
}

function ButtonSoon() {
  const { t } = useT();

  return (
    <Button
      accent={"dark200"}
      appearance={"solid"}
      isDisabled
      isPreview
      isMini
      purpose={"button"}
      title={_.startCase(t("structs.featureComingSoon"))}
    />
  );
}

function Body() {
  const { chainId, isConfigured, proxy } = useAccount();
  const isProxyAvailable = useMemo(() => _.isEthereumAddress(proxy), [proxy]);
  const { t } = useT();

  return (
    <Provider>
      <Wrapper>
        <Content>
          <Section>
            <Label
              icon={InformationCircleIcon}
              isIconLast
              tooltip={{
                value: <Info chainId={chainId} proxy={proxy} />,
                trigger: "hover",
                color: "dark000",
                arrow: true,
              }}
              value={t("modalCards.proxy.bank.title")}
            />
            <Description>
              <p>{t("modalCards.proxy.bank.description")}</p>
            </Description>
            {FEATURE_EMPTY_PROXY_ENABLED ? (
              isConfigured ? (
                isProxyAvailable ? (
                  <Checkout>
                    <Form />
                    <Confirmation />
                  </Checkout>
                ) : (
                  <ButtonMissing />
                )
              ) : (
                <Loader>
                  <Spinner color={"white"} />
                </Loader>
              )
            ) : (
              <ButtonSoon />
            )}
          </Section>
        </Content>
      </Wrapper>
    </Provider>
  );
}

export default Body;
